import React from "react";
import { Link } from "react-router";
import styled, { css } from "styled-components";
import { config } from "@core/config/config";
import { Path } from "@core/routing/path";
import { Tooltipable } from "@ui/tooltips/tooltipable";
import { Primary, PrimaryMedium } from "@ui/components/containers/primary";
import { ExploreIcon, FeedIcon, HomeIcon, MessaggingIcon } from "@ui/components/icons";
import { row } from "@ui/components/layout";
import { useTopBarNavigation } from "./useTopBarNavigation";

type TopBarNavigation = ReturnType<typeof useTopBarNavigation>;

interface TopBarDesktopRoutesProps {
	navigation: TopBarNavigation;
	Button: React.ComponentType<{ selected: boolean; onClick?: () => void; children?: React.ReactNode }>;
	ButtonLabel: React.ComponentType<{ selected?: boolean; children?: React.ReactNode }>;
	creationMenu: React.ReactNode;
}

export const TopBarDesktopRoutes: React.FC<TopBarDesktopRoutesProps> = ({
	navigation,
	Button,
	ButtonLabel,
	creationMenu,
}) => {
	const { goToHome, goToSearch, isHomeSelected, isSearchSelected, isJaangal, homeLabel, homeTooltip, exploreLabel } =
		navigation;

	return (
		<>
			<Tooltipable text={homeTooltip} offset={-3} dark={true}>
				<Button selected={isHomeSelected} onClick={goToHome}>
					{isJaangal ? (
						<>
							<FeedIcon />
							<ButtonLabel>{homeLabel}</ButtonLabel>
						</>
					) : (
						<HomeIcon />
					)}
				</Button>
			</Tooltipable>
			<Tooltipable text={exploreLabel} offset={-3} dark={true}>
				<Button selected={isSearchSelected} onClick={goToSearch}>
					<ExploreIcon />
					<ButtonLabel>{exploreLabel}</ButtonLabel>
				</Button>
			</Tooltipable>
			{creationMenu}
		</>
	);
};

interface TopBarMobileNavListProps {
	navigation: TopBarNavigation;
	profileId: string;
	unreadMessages: number;
}

export const TopBarMobileNavList: React.FC<TopBarMobileNavListProps> = ({ navigation, profileId, unreadMessages }) => {
	const { goToHome, goToSearch, isHomeSelected, isSearchSelected, isJaangal, homeLabel, exploreLabel, messagesLabel } =
		navigation;

	return (
		<>
			<MobileNavItem selected={isHomeSelected} onClick={goToHome}>
				<IconWrapper>{isJaangal ? <FeedIcon /> : <HomeIcon />}</IconWrapper>
				<PrimaryMedium>{homeLabel}</PrimaryMedium>
			</MobileNavItem>
			<MobileNavItem selected={isSearchSelected} onClick={goToSearch}>
				<IconWrapper>
					<ExploreIcon />
				</IconWrapper>
				<PrimaryMedium>{exploreLabel}</PrimaryMedium>
			</MobileNavItem>
			{config.features.messaging.showInTopBar && (
				<Link to={Path.Chatmessage(profileId)}>
					<MobileNavItem>
						<IconWrapper>
							<MessaggingIcon />
						</IconWrapper>
						<PrimaryMedium>{messagesLabel}</PrimaryMedium>
						{unreadMessages > 0 && (
							<Badge length={`${unreadMessages}`.length}>{unreadMessages > 999 ? 999 : unreadMessages}</Badge>
						)}
					</MobileNavItem>
				</Link>
			)}
		</>
	);
};

const MobileNavItem = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>`
	cursor: pointer;
	border-radius: 9px;
	padding: 16px 10px;
	${row(20, "flex-start", "center")};

	&:hover {
		background-color: ${({ theme }) => theme.colors.bodyBackground};
	}

	> svg path {
		fill: ${({ selected, theme }) => (selected ? theme.colors.primary : theme.colors.textPrimary)};
	}
`;

const IconWrapper = styled.div`
	flex-shrink: 0;
	border-radius: 9px;
	width: 34px;
	height: 34px;
	background-color: ${({ theme }) => theme.colors.secondaryLight};
	${row(0, "center", "center")};

	> svg path {
		fill: ${({ theme }) => theme.colors.secondary};
	}
`;

const Badge = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "length",
})<{ length: number }>`
	${({ length }) =>
		length < 2
			? css`
					width: 14px;
					height: 14px;
				`
			: css`
					min-width: 20px;
					height: 14px;
					padding: 0 4px;
				`}
	border-radius: 7px;
	background-color: ${({ theme }) => theme.colors.red};
	color: ${({ theme }) => theme.colors.white};
	font-size: 10px;
	font-weight: bold;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-left: auto;
`;

export const TopBarButtonLabel = styled(Primary).withConfig({
	shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>`
	color: ${({ theme, selected }) => (selected ? theme.colors.primary : theme.colors.textPrimary)};
	font-weight: bold;
`;
